/**
 * The field repository — every write a crew can make, and the only place they are made.
 *
 * The rule this file exists to hold: **a local write and its outbox op are one
 * transaction.** `OutboxStore.enqueue` says so in its own docs ("must be atomic with the
 * caller's local write"), and only the caller can honour that, because only the caller
 * owns both tables. Split them and a phone dying in between produces one of two bad
 * outcomes: a photo the rep can see that the office will never receive, or an obligation
 * to upload a photo that is not in the local record.
 *
 * Reads are plain queries. Writes go through here.
 */

import Dexie from 'dexie';

import {
  buildOp,
  capturePhoto,
  checklistProgress,
  inspectionTemplate,
  webCryptoSha256,
  type CaptureInput,
  type ChecklistProgress,
  type InspectionKind,
  type Sha256,
  type SlottedPhoto,
  type Uuid,
} from '@rush/sync-core';

import type { JobRow, PhotoRow, RushDatabase } from './database.js';
import { DexieOutboxStore, DexiePhotoBlobStore } from './database.js';

/** Who is capturing, and on what. Supplied once at startup. */
export interface FieldIdentity {
  readonly orgId: Uuid;
  readonly actorId: Uuid;
  readonly deviceId: Uuid;
}

export interface NewJobInput {
  readonly kind: InspectionKind;
  readonly address: string;
  readonly homeownerName: string;
  readonly homeownerPhone: string;
  readonly dateOfLoss: string;
  readonly carrier: string;
  readonly claimNumber: string;
}

export interface CapturePhotoInput {
  readonly jobId: Uuid;
  readonly slotId: string;
  readonly bytes: Uint8Array;
  readonly contentType: string;
  readonly width?: number;
  readonly height?: number;
}

export interface JobView {
  readonly job: JobRow;
  readonly photos: readonly PhotoRow[];
  readonly progress: ChecklistProgress;
}

/**
 * How safe one photo actually is, from the crew's point of view.
 *
 * Deliberately not a boolean, and deliberately not the outbox status renamed. A rep asking
 * "is my roof uploaded?" is asking whether the evidence would survive this phone going in a
 * puddle, and that question has more than two answers:
 *
 *  - `queued` — the metadata op has not been confirmed. This phone is the only copy.
 *  - `recorded` — the server has the metadata; the *bytes* are still only here. The office
 *    can see that a photo exists and cannot open it.
 *  - `secured` — metadata confirmed and the local bytes released, which only happens after
 *    object storage confirmed them against the capture checksum. The one state where
 *    losing the phone loses nothing.
 *  - `blocked` — the op was terminally rejected. It will not retry without operator action,
 *    and pretending otherwise is how a crew is told everything is fine over evidence that
 *    will never leave the device.
 *
 * `secured` is unreachable in this build — the presigned upload path from ADR-0001 §4.6 has
 * nowhere to upload to yet (ADR-0002 §2). It exists here so that when object storage lands,
 * the screen gains a true state rather than the other states quietly changing meaning.
 */
export type PhotoSyncState = 'queued' | 'recorded' | 'secured' | 'blocked';

export interface PhotoStatus {
  readonly photoId: Uuid;
  readonly state: PhotoSyncState;
  /** Whether the bytes still exist only on this device. */
  readonly bytesHeldLocally: boolean;
  /** Present when `blocked`, so the screen can say what actually went wrong. */
  readonly error?: string;
}

export interface JobSummary {
  readonly job: JobRow;
  readonly photoCount: number;
  /** Photos whose metadata the server has not confirmed. */
  readonly queued: number;
  /** Photos frozen by a terminal rejection. Needs a human. */
  readonly blocked: number;
  readonly requiredSatisfied: number;
  readonly requiredTotal: number;
}

export interface SyncCounts {
  readonly pending: number;
  readonly synced: number;
  readonly failed: number;
  /** Photo byte payloads still held only on this device. */
  readonly photosHeldLocally: number;
  readonly bytesHeldLocally: number;
}

export class FieldRepository {
  readonly #db: RushDatabase;
  readonly #outbox: DexieOutboxStore;
  readonly #blobs: DexiePhotoBlobStore;
  readonly #identity: FieldIdentity;
  readonly #nextId: () => Uuid;
  readonly #now: () => number;
  readonly #sha256: Sha256;

  constructor(options: {
    db: RushDatabase;
    outbox: DexieOutboxStore;
    blobs: DexiePhotoBlobStore;
    identity: FieldIdentity;
    nextId: () => Uuid;
    now?: () => number;
    sha256?: Sha256;
  }) {
    this.#db = options.db;
    this.#outbox = options.outbox;
    this.#blobs = options.blobs;
    this.#identity = options.identity;
    this.#nextId = options.nextId;
    this.#now = options.now ?? (() => Date.now());
    this.#sha256 = options.sha256 ?? webCryptoSha256;
  }

  /**
   * Creates a job locally and queues its `create` op in the same transaction.
   *
   * No network call, no spinner, no failure path for being offline. A rep standing in a
   * driveway with no signal starts the job and gets to work.
   */
  async createJob(input: NewJobInput): Promise<JobRow> {
    const jobId = this.#nextId();
    const row: JobRow = {
      jobId,
      orgId: this.#identity.orgId,
      kind: input.kind,
      address: input.address,
      homeownerName: input.homeownerName,
      homeownerPhone: input.homeownerPhone,
      dateOfLoss: input.dateOfLoss,
      carrier: input.carrier,
      claimNumber: input.claimNumber,
      createdAt: this.#now(),
    };

    const op = buildOp({
      opId: this.#nextId(),
      orgId: this.#identity.orgId,
      entityType: 'job',
      entityId: jobId,
      opType: 'create',
      payload: {
        kind: input.kind,
        address: input.address,
        homeownerName: input.homeownerName,
        homeownerPhone: input.homeownerPhone,
        dateOfLoss: input.dateOfLoss,
        carrier: input.carrier,
        claimNumber: input.claimNumber,
      },
      clientCreatedAt: new Date(row.createdAt).toISOString(),
      deviceId: this.#identity.deviceId,
      actorId: this.#identity.actorId,
    });

    await this.#db.transaction(
      'rw',
      [this.#db.jobs, this.#db.outbox, this.#db.counters],
      async () => {
        await this.#db.jobs.add(row);
        await this.#outbox.enqueue(op);
      },
    );

    return row;
  }

  /**
   * Captures a photo into a checklist slot.
   *
   * Two phases, and the split is deliberate. Hashing and writing the bytes happen *before*
   * the transaction, because a multi-megabyte write inside an IndexedDB transaction holds
   * locks long enough to jam the UI on a mid-range Android — and because bytes-without-a-row
   * is the failure we can recover from. The row and the op then land together.
   */
  async capture(input: CapturePhotoInput): Promise<PhotoRow> {
    const captureInput: CaptureInput = {
      jobId: input.jobId,
      slotId: input.slotId,
      bytes: input.bytes,
      contentType: input.contentType,
      ...(input.width === undefined ? {} : { width: input.width }),
      ...(input.height === undefined ? {} : { height: input.height }),
    };

    const captured = await capturePhoto(
      captureInput,
      {
        orgId: this.#identity.orgId,
        actorId: this.#identity.actorId,
        deviceId: this.#identity.deviceId,
        nextId: this.#nextId,
        now: this.#now,
        sha256: this.#sha256,
      },
      this.#blobs,
    );

    const row: PhotoRow = {
      photoId: captured.photoId,
      jobId: input.jobId,
      slotId: input.slotId,
      sha256: captured.sha256,
      byteLength: captured.byteLength,
      contentType: captured.contentType,
      capturedAt: captured.op.clientCreatedAt,
      redacted: false,
    };

    await this.#db.transaction(
      'rw',
      [this.#db.photos, this.#db.outbox, this.#db.counters],
      async () => {
        await this.#db.photos.add(row);
        await this.#outbox.enqueue(captured.op);
      },
    );

    return row;
  }

  /**
   * Marks a photo redacted.
   *
   * Not a delete, and there is no delete. The row stays, the bytes stay, the original op
   * stays in the log; a flag is set and the slot reopens so the rep is told to reshoot.
   * ADR-0001 §4.4 — the client is not allowed to express destruction of evidence.
   */
  async redactPhoto(photoId: Uuid, reason: string): Promise<void> {
    const op = buildOp({
      opId: this.#nextId(),
      orgId: this.#identity.orgId,
      entityType: 'photo',
      entityId: photoId,
      opType: 'redact',
      payload: { reason },
      clientCreatedAt: new Date(this.#now()).toISOString(),
      deviceId: this.#identity.deviceId,
      actorId: this.#identity.actorId,
    });

    await this.#db.transaction(
      'rw',
      [this.#db.photos, this.#db.outbox, this.#db.counters],
      async () => {
        const existing = await this.#db.photos.get(photoId);
        if (existing === undefined) throw new Error(`unknown photoId: ${photoId}`);
        await this.#db.photos.put({ ...existing, redacted: true });
        await this.#outbox.enqueue(op);
      },
    );
  }

  // -- reads ----------------------------------------------------------------

  async listJobs(): Promise<readonly JobRow[]> {
    return await this.#db.jobs.orderBy('createdAt').reverse().toArray();
  }

  async getJob(jobId: Uuid): Promise<JobView | undefined> {
    const job = await this.#db.jobs.get(jobId);
    if (job === undefined) return undefined;

    const photos = await this.#db.photos.where('jobId').equals(jobId).toArray();
    const kind = job.kind as InspectionKind;
    const slotted: SlottedPhoto[] = photos.map((p) => ({ slotId: p.slotId, redacted: p.redacted }));

    return { job, photos, progress: checklistProgress(inspectionTemplate(kind), slotted) };
  }

  async photoBytes(photoId: Uuid): Promise<Uint8Array | undefined> {
    return await this.#blobs.get(photoId);
  }

  /**
   * Per-photo sync state for one job.
   *
   * Derived from the outbox rather than stored on the photo row, on purpose. A stored
   * status is a second copy of the truth that drifts the first time a write is missed, and
   * the thing it would be lying about is whether somebody's evidence is safe. The outbox is
   * the only record of what the server has confirmed, so the screen reads it directly.
   *
   * The per-photo rule is pessimistic in both directions: any failed op blocks the photo,
   * and any unconfirmed op keeps it queued. A photo whose `create` landed but whose
   * `redact` is still pending is not settled, and saying otherwise would show a rep a
   * redaction the office has not actually been told about.
   */
  async photoStatuses(jobId: Uuid): Promise<ReadonlyMap<Uuid, PhotoStatus>> {
    const photoIds = (await this.#db.photos.where('jobId').equals(jobId).primaryKeys()) as Uuid[];
    return await this.#statusesFor(photoIds);
  }

  /** The job list: enough per job to show progress and what is still owed to the office. */
  async jobSummaries(): Promise<readonly JobSummary[]> {
    const jobs = await this.listJobs();
    const summaries: JobSummary[] = [];

    for (const job of jobs) {
      const photos = await this.#db.photos.where('jobId').equals(job.jobId).toArray();
      const statuses = await this.#statusesFor(photos.map((p) => p.photoId));
      const slotted: SlottedPhoto[] = photos.map((p) => ({
        slotId: p.slotId,
        redacted: p.redacted,
      }));
      const progress = checklistProgress(inspectionTemplate(job.kind as InspectionKind), slotted);

      let queued = 0;
      let blocked = 0;
      for (const status of statuses.values()) {
        if (status.state === 'queued') queued += 1;
        if (status.state === 'blocked') blocked += 1;
      }

      summaries.push({
        job,
        photoCount: photos.length,
        queued,
        blocked,
        requiredSatisfied: progress.requiredSatisfied,
        requiredTotal: progress.requiredTotal,
      });
    }

    return summaries;
  }

  async #statusesFor(photoIds: readonly Uuid[]): Promise<ReadonlyMap<Uuid, PhotoStatus>> {
    const statuses = new Map<Uuid, PhotoStatus>();
    if (photoIds.length === 0) return statuses;

    const rows = await this.#db.outbox
      .where('entityId')
      .anyOf(photoIds as string[])
      .toArray();
    const held = new Set(await this.#blobs.listPendingUpload());

    const byEntity = new Map<Uuid, typeof rows>();
    for (const row of rows) {
      const bucket = byEntity.get(row.entityId);
      if (bucket === undefined) byEntity.set(row.entityId, [row]);
      else bucket.push(row);
    }

    for (const photoId of photoIds) {
      const ops = byEntity.get(photoId) ?? [];
      const bytesHeldLocally = held.has(photoId);
      const failed = ops.find((row) => row.status === 'failed');
      // No ops at all is not a real state — capture writes the row and the op in one
      // transaction — but if it ever happens, "not confirmed" is the safe reading.
      const unconfirmed = ops.length === 0 || ops.some((row) => row.status !== 'synced');

      let state: PhotoSyncState;
      if (failed !== undefined) state = 'blocked';
      else if (unconfirmed) state = 'queued';
      else state = bytesHeldLocally ? 'recorded' : 'secured';

      statuses.set(photoId, {
        photoId,
        state,
        bytesHeldLocally,
        ...(failed?.lastError === undefined ? {} : { error: failed.lastError.message }),
      });
    }

    return statuses;
  }

  /** The numbers the sync banner shows. One query pass, called on every render. */
  async syncCounts(): Promise<SyncCounts> {
    const byStatus = await this.#outbox.countByStatus();
    const held = await this.#blobs.listPendingUpload();
    const bytes = await this.#blobs.totalBytes();
    return {
      pending: byStatus.pending,
      synced: byStatus.synced,
      failed: byStatus.failed,
      photosHeldLocally: held.length,
      bytesHeldLocally: bytes,
    };
  }
}

/** Wires a repository over an open database. */
export function createRepository(
  db: RushDatabase,
  identity: FieldIdentity,
  nextId: () => Uuid,
): FieldRepository {
  return new FieldRepository({
    db,
    outbox: new DexieOutboxStore(db),
    blobs: new DexiePhotoBlobStore(db),
    identity,
    nextId,
  });
}

export { Dexie };
